/**
 * The write affordances on the scene, drawn above the cards from the scene's own
 * geometry (specs/tki-kuutio-flow.md → Renderer, phase B): the (+) at a card's right
 * edge, the knot on a causal connector where a blocker can stand, and the handle on a
 * card's top edge that drags a link to another card. Each hands the map view the same
 * arguments the layered renderer did — the scene element and a screen position — and
 * the view's existing flows (effect type → create card, blocker card, link kind) do the
 * rest. Nothing here writes or lays out.
 */
import React, { useMemo, useRef, useState } from 'react';

const ADD_RADIUS = 13;
const KNOT_HIT = 16;
const HANDLE_RADIUS = 6;
const HANDLE_HIT = 16;
const ADD_KINDS = new Set(['node', 'goal', 'start', 'blocker', 'avoider']);
const HANDLE_KINDS = new Set(['node', 'goal', 'blocker', 'avoider']);
const BLOCKER = '#EF5350';

export default function SceneInteractions({ scene, showAdd = false, showKnots = false, showHandles = false, onAdd, onKnot, onLink }) {
  const gRef = useRef(null);
  const [drag, setDrag] = useState(null); // { fromId, x, y, targetId } in scene (SVG) coordinates

  const cards = scene.cards;
  const edgeById = useMemo(() => new Map(scene.edges.map((e) => [e.id, e])), [scene.edges]);
  // Where a blocker could stand: a tree connector's knot, drawn once per edge; on a
  // trunk, once per branch into a target (the bundle's labels carry the knots).
  const knots = useMemo(() => {
    if (!showKnots) return [];
    const out = scene.edges.filter((e) => e.kind === 'primary' && e.knot && !e.bundle).map((e) => ({ key: e.id, edge: e, point: e.knot }));
    scene.bundles.forEach((b) => b.labels.forEach((l) => {
      const edge = b.members.map((id) => edgeById.get(id)).find((e) => e && e.to === l.to);
      if (edge && l.knot) out.push({ key: `${b.id}:${l.to}`, edge, point: l.knot });
    }));
    return out;
  }, [scene, showKnots, edgeById]);
  const addable = showAdd ? cards.filter((c) => ADD_KINDS.has(c.kind) && !c.level) : [];
  const handles = showHandles ? cards.filter((c) => HANDLE_KINDS.has(c.kind) && !c.level) : [];

  const toSvg = (clientX, clientY) => {
    const ctm = gRef.current?.getScreenCTM();
    const svg = gRef.current?.ownerSVGElement;
    if (!ctm || !svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    return pt.matrixTransform(ctm.inverse());
  };
  const boxAt = (p, exceptId) => handles.find((c) => c.id !== exceptId && p.x >= c.x && p.x <= c.x + c.w && p.y >= c.y - 14 && p.y <= c.y + c.h + 14) || null;

  const startDrag = (e, card) => {
    e.stopPropagation();
    e.preventDefault();
    const p = toSvg(e.clientX, e.clientY);
    if (!p) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not capturable */ }
    setDrag({ fromId: card.id, x: p.x, y: p.y, targetId: null });
  };
  const moveDrag = (e) => {
    const p = toSvg(e.clientX, e.clientY);
    if (!p) return;
    setDrag((d) => (d ? { ...d, x: p.x, y: p.y, targetId: boxAt(p, d.fromId)?.id || null } : d));
  };
  const endDrag = (e) => {
    if (!drag) return;
    e.stopPropagation();
    const { fromId, targetId } = drag;
    setDrag(null);
    if (!targetId) return;
    onLink?.(cards.find((c) => c.id === fromId), cards.find((c) => c.id === targetId), { x: e.clientX, y: e.clientY });
  };
  const from = drag ? cards.find((c) => c.id === drag.fromId) : null;

  return (
    <g ref={gRef} className="scene-interactions" onPointerMove={drag ? moveDrag : undefined} onPointerUp={drag ? endDrag : undefined}>
      {drag && <rect x={-100000} y={-100000} width={200000} height={200000} fill="transparent" />}
      {addable.map((c) => (
        <g
          key={`add:${c.id}`}
          className="scene-add"
          transform={`translate(${c.x + c.w + ADD_RADIUS + 6}, ${c.cy})`}
          style={{ cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); onAdd?.(c, { x: e.clientX, y: e.clientY, svgX: c.x + c.w, svgY: c.cy }); }}
        >
          <circle r={ADD_RADIUS} fill="#0b0d12" stroke="#5b6478" strokeWidth={1.5} />
          <path d={`M -6 0 H 6 M 0 -6 V 6`} stroke="#e8ebf2" strokeWidth={2} strokeLinecap="round" />
        </g>
      ))}
      {knots.map(({ key, edge, point }) => (
        <g
          key={`knot:${key}`}
          className="scene-knot"
          transform={`translate(${point.x}, ${point.y})`}
          style={{ cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); onKnot?.(edge, { x: e.clientX, y: e.clientY }); }}
        >
          <circle r={KNOT_HIT} fill="transparent" />
          <circle r={5} fill={BLOCKER} stroke="#0b0d12" strokeWidth={1.5} />
        </g>
      ))}
      {handles.map((c) => (
        <g
          key={`handle:${c.id}`}
          className="scene-handle"
          transform={`translate(${c.cx}, ${c.y})`}
          style={{ cursor: 'grab' }}
          onPointerDown={(e) => startDrag(e, c)}
        >
          <circle r={HANDLE_HIT} fill="transparent" />
          <circle r={HANDLE_RADIUS} fill="#4cc7ce" stroke="#0b0d12" strokeWidth={1.5} />
        </g>
      ))}
      {drag && from && (
        <g pointerEvents="none">
          <line x1={from.cx} y1={from.y} x2={drag.x} y2={drag.y} stroke="#4cc7ce" strokeWidth={2} strokeDasharray="6 5" />
          {drag.targetId && (() => {
            const t = cards.find((c) => c.id === drag.targetId);
            return t ? <rect x={t.x - 4} y={t.y - 4} width={t.w + 8} height={t.h + 8} rx={12} fill="none" stroke="#4cc7ce" strokeWidth={3} /> : null;
          })()}
        </g>
      )}
    </g>
  );
}
